import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectEffectiveDate,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateADUAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];
  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];
  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { adu } = subjectImprovements;
  const { adu: compADU } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) return undefined;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getADUAdj({
    subject:
      adu && adu.area && isDefined(adu.quality)
        ? {
            sqft: AloftArea.schema.parse(adu.area),
            quality: adu.quality,
            fullBaths: adu.fullBaths,
            halfBaths: adu.halfBaths,
            zipCode,
          }
        : undefined,
    comp:
      compADU && compADU.area && isDefined(compADU.quality)
        ? {
            sqft: AloftArea.schema.parse(compADU.area),
            quality: compADU.quality,
            fullBaths: compADU.fullBaths,
            halfBaths: compADU.halfBaths,
            zipCode: compZipCode,
          }
        : undefined,
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectADUAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'adu'),
    selectEffectiveDate,
    (state, id: string) => id,
  ],
  calculateADUAdj,
);

export const selectADUAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'adu'),
    selectEffectiveDate,
    selectCompSequence,
  ],
  (
    adjustmentVersion,
    improvements,
    propertyIdentification,
    depreciationRatios,
    effectiveDate,
    compSequence,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateADUAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        effectiveDate,
        propID,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedADUAdj = createPriceSelector(
  [selectADUAdj, selectAdjustmentSettings],
  (adj, adjSettings) => (adj ? applyRoundingAndThresholds(adj, 'adu', adjSettings) : undefined),
);

export const selectAppliedADUAdjDisplay = createSelector([selectAppliedADUAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedADUAdjsDisplay = createSelector(
  [selectADUAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, id) => {
      const adj = adjs[id];
      const manualAdjustment = manualAdjustmentsEntities[id]?.adu;
      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[id] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[id] = {
          value: getStringValueFromAdjustment(applyRoundingAndThresholds(adj, 'adu', adjSettings)),
          color: getColorFromAdjustment(adj),
        };
      }
      return acc;
    }, {}),
);
